import { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { trackClientEvent } from 'analytics/analytics'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalContent from 'LuxKit/Modal/ModalContent'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import { ModalSteps } from '../defaults'
import TravelPreferencesOptionsGrid from '../TravelPreferencesImageItemsGrid/TravelPreferencesOptionsGrid'
import { getCurrentUserId } from 'selectors/accountSelectors'
import type { UserPreferenceDestination } from '../types'
import { deleteUserPreferenceDestination, updateUserPreferenceDestination } from 'actions/userTravelPreferencesActions'
import GeoContext from 'contexts/geoContext'
import { DEFAULT_FILTER } from 'home/components/TrendingDestinations/TrendingDestinations'
import { useHotelTrendingDestinations } from 'home/hooks/useHotelTrendingDestinations'
import TextInput from 'components/Common/Form/Input/TextInput'
import SearchPlaceToggleList from '../SearchPlaceToggleList'
import { DisplayItemWithCheckBox } from './SearchModalDisplayItemWithCheckBox'

interface Props {
  selectedDestinations: Array<App.UserPreferenceDestination>
  onSetNextStep: (step: ModalSteps, isSaveAction?: boolean) => void
}

function TravelPreferencesModalDestinations(props: Props) {
  const intl = useIntl()
  const { selectedDestinations, onSetNextStep } = props

  const dispatch = useAppDispatch()
  const userId = useAppSelector(getCurrentUserId)
  const [destinationSearch, setDestinationSearch] = useState<string>('')
  const [selectedItems, setSelectedItems] = useState<Array<UserPreferenceDestination>>([])
  const [displayItems, setDisplayItems] = useState<Array<UserPreferenceDestination>>([])
  const selectedPlaceIds = useMemo(() => selectedDestinations?.map(h => h.placeId) || [], [selectedDestinations])
  const { currentRegionCode } = useContext(GeoContext)

  const [trendingDestinations, _] = useHotelTrendingDestinations(DEFAULT_FILTER, currentRegionCode)
  const trendingDestinationsNoSelected: Array<UserPreferenceDestination> = useMemo(() => (
    trendingDestinations
      .filter(destination => !!destination.destinationId && !selectedPlaceIds.includes(destination.destinationId))
      .map((d) => ({
        name: d.name,
        placeId: d.destinationId,
        imageId: d.imageId,
        isAlertSelected: false,
      }))
  ), [trendingDestinations, selectedPlaceIds])

  const trendingDestinationsImageMap = useMemo(() => new Map(trendingDestinations.map(item => [item.destinationId, item.imageId])), [trendingDestinations])
  const selectedDestinationItems: Array<UserPreferenceDestination> = useMemo(() => {
    return selectedDestinations?.map(d => ({
      placeId: d.placeId,
      isAlertSelected: d.isAlertSelected || false,
      imageId: trendingDestinationsImageMap.get(d.placeId),
    }))
  }, [selectedDestinations, trendingDestinationsImageMap])

  useEffect(() => {
    setDisplayItems([...selectedDestinationItems, ...trendingDestinationsNoSelected])
  }, [selectedDestinationItems, trendingDestinationsNoSelected])

  useEffect(() => {
    if (selectedDestinationItems) {
      setSelectedItems(selectedDestinationItems)
    }
  }, [selectedDestinationItems])

  const selectedId = useMemo(() => new Set(selectedItems.map((i) => i.placeId)), [selectedItems])

  const onToggleSelection = useCallback((destination: UserPreferenceDestination) => {
    setDestinationSearch('')
    const selected = selectedId.has(destination.placeId)

    // if the users add item which is not in the trending, add the new item in front of the display
    // but if the users remove item, don't remove it from the display in case the grid jumps
    if (!selected && !trendingDestinations.some(i => i.destinationId === destination.placeId)) {
      setDisplayItems([destination, ...displayItems])
    }

    setSelectedItems(prevSelected => {
      if (selected) {
        return prevSelected.filter(item => item.placeId !== destination.placeId)
      }

      return [destination, ...prevSelected]
    })
  }, [selectedId, trendingDestinations, displayItems])

  const onSkip = useCallback(() => {
    onSetNextStep(ModalSteps.hotelBrands)
  }, [onSetNextStep])

  const onUpdatePreferences = useCallback(() => {
    trackClientEvent({
      subject: 'destinations',
      action: 'completion',
      type: 'interaction',
      category: 'travel_preferences',
    })

    for (const item of selectedItems) {
      if (!selectedPlaceIds.includes(item.placeId)) {
        dispatch(updateUserPreferenceDestination(userId!, item.placeId))
      }
    }

    for (const itemId of selectedPlaceIds) {
      if (!selectedItems.some((i) => i.placeId === itemId)) {
        dispatch(deleteUserPreferenceDestination(userId!, itemId))
      }
    }
    onSetNextStep(ModalSteps.hotelBrands, true)
  }, [dispatch, onSetNextStep, selectedPlaceIds, selectedItems, userId])

  useEffect(() => {
    trackClientEvent({
      subject: 'destinations',
      action: 'impression',
      type: 'nonInteraction',
      category: 'travel_preferences',
    })
  }, [])

  return (
    <>
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={16}>
            <BodyText variant="medium">{intl.formatMessage({
              id: 'EsmpWxsiSLfL3cv9aIeT',
              defaultMessage: 'Be the first to know about the best offers in these locations.',
              description: 'Description for destinations step in travel preferences modal',
            })}
            </BodyText>
            <TextInput
              placeholder={intl.formatMessage({
                id: 'i55kfBe4YarCw9cwox5i',
                defaultMessage: 'Search destinations',
                description: 'Placeholder text for destination search input in travel preferences modal',
              })}
              endIcon={<LineSearchIcon />}
              value={destinationSearch}
              onChange={(e) => setDestinationSearch(e.target.value)}
              autoFocus
            />
            <Group direction="vertical" gap={12}>
              {!!destinationSearch && [...selectedItems].map((destination) => <DisplayItemWithCheckBox
                key={`displayItemWithCheckBox-${destination.placeId}`}
                destination={destination}
                onClick={onToggleSelection}
              />)}
              <SearchPlaceToggleList
                searchPhrase={destinationSearch}
                selectedPlaces={selectedItems}
                onSelect={onToggleSelection}
              />
            </Group>
            <TravelPreferencesOptionsGrid
              items={displayItems}
              onSelect={onToggleSelection}
              selectedId={selectedId}
            />
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter primaryActionProps={{ children: intl.formatMessage({ id: 'FWHHvqcnF-Zs0EwUSjBW', defaultMessage: 'Save & continue', description: 'Button to save destinations and continue to next step' }), onClick: onUpdatePreferences }} secondaryActionProps={{ children: intl.formatMessage({ id: 'JFgDP1PsNGTOBdHlPRmt', defaultMessage: 'Skip', description: 'Action button to skip the destinations selection step in travel preferences' }), onClick: onSkip }} />
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  selectedDestinations: state.userTravelPreferences.destinations.data,
})

export default connect(mapStateToProps)(TravelPreferencesModalDestinations)
